// @flow

import * as React from "react";
import { equals, identity, omit } from "ramda";
import moment from "moment";

import { CalendarIcon, ClickOutside, DateField } from "@quantum/ui";

import { useRafLoop } from "react-use";

import InputMask from "react-input-mask";

import Portal from "../Portal";

import "./date-calendar-picker.css";

const DateMaskInput = props => (
  <InputMask {...props} mask="99/99/9999" alwaysShowMask={true} />
);

const hidden = { visibility: "hidden" };
const DateCalendarPickerPortal = ({ disabled, ...props }: any) => {
  const pickerRef = React.useRef();
  const [pickerStyle, setPickerStyle] = React.useState({
    position: "absolute",
    top: 0,
    left: 0,
    width: "0px",
  });

  const isDisplayNone = (element: any) => {
    if (window.getComputedStyle(element).display === "none") return true;
    if (element === document.body) return false;
    return isDisplayNone(element.parentElement);
  };

  useRafLoop(() => {
    if (pickerRef.current) {
      const { top, left, width } = pickerRef.current.getBoundingClientRect();
      const newPickerStyle = {
        position: "absolute",
        top: top + (window.scrollY || window.pageYOffset),
        left: left + (window.scrollX || window.pageXOffset),
        width: `${width}px`,
        display: isDisplayNone(pickerRef.current) ? "none" : "block",
      };
      if (!equals(pickerStyle, newPickerStyle)) setPickerStyle(newPickerStyle);
    }
  }, [pickerRef, pickerStyle, setPickerStyle]);

  return (
    <div>
      <div style={hidden}>
        <DateField
          disableCloseOnClickOutside
          disabled={disabled}
          {...omit(
            ["isValidDate", "onChange", "onFocus", "onBlur", "value"],
            props
          )}
          open={false}
          renderInput={inputProps => (
            <div className="DateCalendarPicker" ref={pickerRef}>
              <DateMaskInput {...inputProps} onBlur={props.onBlur} />
              <CalendarIcon />
            </div>
          )}
        />
      </div>
      <Portal>
        <div style={pickerStyle}>
          <DateCalendarPickerNonPortal disabled={disabled} {...props} />
        </div>
      </Portal>
    </div>
  );
};

const DateCalendarPickerNonPortal = ({
  disabled,
  value,
  isValidDate,
  restoreOnInvalid = true,
  onChange: onChangeDate,
  onBlur: onOriginalBlur,
  onFocus: onOriginalFocus,
  ...props
}: any) => {
  const [open, setOpen] = React.useState(props.open || false);
  const openPicker = React.useCallback(() => setOpen(true), [setOpen]);
  const closePicker = React.useCallback(() => setOpen(false), [setOpen]);

  // This is required to force `react-datepicker` to update its component, since
  // it has a bug that won't update its input value.
  const [currentKey, setKey] = React.useState(0);

  const focusedRef = React.useRef(false);
  const currValueRef = React.useRef(
    value ? moment(value).format("DD/MM/YYYY") : null
  );
  const prevValueRef = React.useRef();

  const onChange = React.useCallback(
    date => {
      if (date instanceof moment) {
        currValueRef.current = date.format("DD/MM/YYYY");
      } else if (date instanceof Date) {
        currValueRef.current = moment(date).format("DD/MM/YYYY");
      } else if (typeof date === "string") {
        currValueRef.current = date;
      } else {
        currValueRef.current = null;
      }
    },
    [currValueRef]
  );

  const onFocus = React.useCallback(
    (...args) => {
      focusedRef.current = true;
      prevValueRef.current = value;
      if (onOriginalFocus) onOriginalFocus(...args);
    },
    [value, focusedRef, prevValueRef, onOriginalFocus]
  );

  const onCalendarClick = React.useCallback(() => {
    focusedRef.current = true;
    prevValueRef.current = value;
    openPicker();
  }, [focusedRef, prevValueRef, value, openPicker]);

  const commitChangesAndExit = React.useCallback(
    date => {
      if (isValidDate(date)) {
        onChangeDate(date);
      } else if (restoreOnInvalid) {
        onChangeDate(prevValueRef.current);
        setKey(key => key + 1);
      }
      onOriginalBlur && onOriginalBlur();
      closePicker();
      focusedRef.current = false;
    },
    [
      closePicker,
      isValidDate,
      restoreOnInvalid,
      onChangeDate,
      onOriginalBlur,
      focusedRef,
    ]
  );

  const onBlur = React.useCallback(
    (...args) => {
      if (!focusedRef.current) return;

      const currentDate = currValueRef.current
        ? moment(currValueRef.current, "DD/MM/YYYY")
        : null;

      commitChangesAndExit(currentDate);
    },
    [commitChangesAndExit, focusedRef, currValueRef]
  );

  const onDatePickerBlur = React.useCallback(
    date => {
      const currentDate =
        currValueRef.current || date
          ? moment(currValueRef.current || date, "DD/MM/YYYY")
          : null;

      commitChangesAndExit(currentDate);
    },
    [commitChangesAndExit, currValueRef]
  );

  return (
    <ClickOutside onClickOutside={onBlur}>
      <DateField
        key={currentKey}
        closeOnSelect={true}
        disabled={disabled}
        {...props}
        isValidDate={isValidDate}
        value={value ? moment(value) : null}
        onChange={onChange}
        open={open}
        onFocus={openPicker}
        onBlur={onDatePickerBlur}
        renderInput={inputProps => (
          <div
            className="DateCalendarPicker"
            onClick={disabled ? identity : onCalendarClick}
          >
            <DateMaskInput {...inputProps} onFocus={onFocus} />
            <CalendarIcon />
          </div>
        )}
      />
    </ClickOutside>
  );
};

const DateCalendarPicker = ({ portal = true, ...props }: any) =>
  portal ? (
    <DateCalendarPickerPortal {...props} />
  ) : (
    <DateCalendarPickerNonPortal {...props} />
  );

export default DateCalendarPicker;
